import { computed, observable, action } from 'mobx'
import { orderBy } from 'lodash-es'
import { type Document } from '@/features/documents/Document'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { type UploadBarViewModel } from '@/features/documents/UploadBarViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import type { InternalCreateDocumentViewModelFn } from './internal'
import { type ColumnSortOrder } from './document-table-columns'

export interface DocsTableColumn {
  name:
    | 'name'
    | 'workspace'
    | 'documentType'
    | 'dateModified'
    | 'actions'
    | 'fileType'
  header: string
  key:
    | keyof Pick<
        Document,
        'title' | 'dateCreated' | 'fileType' | 'dateModified'
      >
    | 'actions'
    | 'workspace.id'
    | 'documentType.name'
  sortable?: boolean
}
export const docsTableColumns: DocsTableColumn[] = [
  {
    name: 'name',
    header: 'Name',
    key: 'title',
    sortable: true,
  },
  {
    name: 'workspace',
    header: 'Workspace',
    key: 'workspace.id',
    sortable: true,
  },
  {
    name: 'documentType',
    header: 'Document Type',
    key: 'documentType.name',
  },
  {
    name: 'fileType',
    header: 'Type',
    key: 'fileType',
    sortable: true,
  },
  {
    name: 'dateModified',
    header: 'Last Updated',
    key: 'dateModified',
    sortable: true,
  },
]

/**
 * How the Documents Center table is searched and sorted.
 */
export interface DocumentsListView {
  searchText: string
  sortOrder: ColumnSortOrder
  sortField: DocsTableColumn['key']
}

/**
 * View model for the Documents Center table.
 */
export class DocumentsListViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _documentVms

  @observable accessor searchText = defaultDocumentsListView.searchText
  @observable accessor sortOrder = defaultDocumentsListView.sortOrder
  @observable accessor sortField = defaultDocumentsListView.sortField

  constructor(
    private readonly _getDocuments: () => readonly Document[],
    private readonly _getDocumentsWorkspaceForFiltering: () =>
      Workspace | undefined,
    uploadBarVm: UploadBarViewModel,
    createDocumentViewModel: InternalCreateDocumentViewModelFn,
  ) {
    this._documentVms = new ViewModelList({
      models: () => this.filteredDocuments,
      create: (document) => createDocumentViewModel(document, uploadBarVm),
      activate: (vm) => vm.activate(),
      deactivate: (vm) => vm.deactivate(),
    })

    this._lifecycle.manage(this._documentVms)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  @computed
  get columns() {
    if (!this.selectedWorkspace) {
      return docsTableColumns
    }

    return docsTableColumns.filter((c) => c.name !== 'workspace')
  }

  isColumnHeaderVisible(colName: DocsTableColumn['name']) {
    return this.columns.map((col) => col.name).includes(colName)
  }

  @computed
  get documents() {
    return this._getDocuments()
  }

  @computed
  get selectedWorkspace() {
    return this._getDocumentsWorkspaceForFiltering()
  }

  @computed
  get workspaceDocs() {
    const workspace = this.selectedWorkspace
    if (!workspace) {
      return this.documents
    }

    return this.documents.filter((doc) => doc.workspaceId === workspace.id)
  }

  /**
   * The searched and sorted documents.
   */
  @computed
  get filteredDocuments() {
    let filtered = this.workspaceDocs

    if (this.searchText !== '') {
      filtered = filtered.filter((doc) =>
        doc.title.toLowerCase().includes(this.searchText.toLowerCase()),
      )
    }

    return orderBy(
      filtered,
      [this.sortField],
      this.sortOrder === 'none' ? undefined : this.sortOrder,
    )
  }

  /**
   * View models for the searched and sorted documents.
   */
  @computed
  get documentVms() {
    return this._documentVms.items
  }

  /**
   * The table's column headers with their sort order.
   */
  @computed
  get columnHeaders() {
    return this.columns.map((col) => ({
      key: col.key,
      header: col.header,
      sortable: col.sortable,
      sortOrder: col.key === this.sortField ? this.sortOrder : 'none',
    }))
  }

  /**
   * Whether the table shows each document's workspace.
   */
  @computed
  get showWorkspace() {
    return this.isColumnHeaderVisible('workspace')
  }

  /**
   * Sorts by the column with the given key.
   */
  @action.bound
  setSort(columnKey: string, sortOrder: ColumnSortOrder) {
    const column = this.columns.find((c) => c.key === columnKey)
    if (!column) {
      return
    }

    this.sortField = column.key
    this.sortOrder = sortOrder
  }

  @action.bound
  setSearchText(query: string) {
    this.searchText = query
  }

  /**
   * The current search and sort, for restoring with {@link restoreView}.
   */
  get view(): DocumentsListView {
    return {
      searchText: this.searchText,
      sortOrder: this.sortOrder,
      sortField: this.sortField,
    }
  }

  /**
   * Restores a search and sort.
   */
  @action.bound
  restoreView(view: DocumentsListView) {
    this.searchText = view.searchText
    this.sortOrder = view.sortOrder
    this.sortField = view.sortField
  }
}

/**
 * The search and sort the Documents Center table starts with.
 */
export const defaultDocumentsListView: DocumentsListView = {
  searchText: '',
  sortOrder: 'desc',
  sortField: 'dateModified',
}
